import type { Colors } from 'styled-components';
import { useConstant } from '../../hooks';
import { Z_INDEX } from '../../styles';
import { HedgeControlStatusEnum, type CurrencyConversionRate, type IHedgePositionStatus } from '../../types';
import { toBig, toBigWithDefault, type Leaves } from '../../utils';
import { AbbrLimit } from '../AbbrLimit';
import { Box, Flex, HStack, VStack, type BoxProps } from '../Core';
import { InlineFormattedNumber } from '../FormattedNumber';
import { IconName } from '../Icons';
import { LeftMiddleRightFlexbox } from '../LeftMiddleRightFlexbox';
import { Meter } from '../Meter';
import { Table, Tbody, Td, Th, Tr } from '../Table';
import { Text } from '../Text';
import { JITTooltip } from '../Tooltip';
import {
  getPositionThresholdMeterValues,
  type PositionThresholdMeterValuesArgs,
} from './getPositionThresholdMeterPercents';
import { MeterTargetIndicatorWrapper } from './styles';

type PositionThresholdMeterProps = {
  /**
   * The values to be displayed in the meter.
   * If updated, the values will be displayed in italics.
   * Determining if the values are updated is done by checking if the value is in the `isUpdatedRecord` prop.
   */
  value: PositionThresholdMeterValuesArgs;
  isUpdatedRecord?: Record<'LongPositionLimit' | 'ShortPositionLimit' | 'TargetPosition' | 'TargetAsset', boolean>;
  /**
   * Currency to display the estimate in, will carry out conversion if different from the target asset.
   */
  displayCurrency?: string;
  displayCurrencyConversion?: CurrencyConversionRate;
};

export function PositionThresholdMeter({
  value,
  isUpdatedRecord,
  displayCurrency,
  displayCurrencyConversion,
}: PositionThresholdMeterProps) {
  const jitWrapperProps: BoxProps = useConstant({ w: '100%' });

  const { currentPercent, targetPercent } = getPositionThresholdMeterValues(value);
  const positionAtZero = currentPercent === 0;
  const meterColor = iconColorByHedgePositionStatus[value.HedgeControlStatus ?? HedgeControlStatusEnum.Disabled];

  const { shortPositionLimit, longPositionLimit, isEstimate } = getPositionLimits({
    value,
    displayCurrency,
    displayCurrencyConversion,
  });

  return (
    <VStack
      w="100%"
      h="100%"
      py="spacing4"
      justifyContent="space-between"
      fontSize="fontSizeTiny"
      color="colorTextDefault"
      position="relative"
      zIndex={0}
      data-testid="position-threshold-meter"
    >
      <JITTooltip
        usePortal
        zIndex={Z_INDEX.dropdown}
        tooltip={<PositionThresholdMeterTooltipContent value={value} />}
        wrapperProps={jitWrapperProps}
      >
        <Flex flexDirection="column" w="100%" gap="spacing4" position="relative">
          <HStack w="100%" gap="spacing2" mb="1px">
            <MeterTargetIndicatorWrapper
              renderIndicator={targetPercent <= 0}
              targetPercent={targetPercent}
              color={meterColor}
            >
              <Meter
                value={currentPercent < 0 ? currentPercent * -1 : 0}
                showLabel={false}
                alignBar="right"
                showInitialAnimation={false}
                color={meterColor}
                roundStartingEdge
                height={6}
              />
            </MeterTargetIndicatorWrapper>
            <Box
              minWidth="1px"
              background={positionAtZero ? meterColor : 'backgroundProgressBar'}
              alignSelf="stretch"
              my="1px"
            />
            <MeterTargetIndicatorWrapper
              targetPercent={targetPercent}
              color={meterColor}
              renderIndicator={targetPercent > 0}
            >
              <Meter
                value={currentPercent > 0 ? currentPercent : 0}
                showLabel={false}
                color={meterColor}
                showInitialAnimation={false}
                roundStartingEdge
                height={6}
              />
            </MeterTargetIndicatorWrapper>
          </HStack>
          <LeftMiddleRightFlexbox
            left={
              <Text
                fontStyle={isUpdatedRecord?.ShortPositionLimit || isUpdatedRecord?.TargetAsset ? 'italic' : undefined}
                display="flex"
              >
                {isEstimate ? '~' : ''}
                {toBig(shortPositionLimit)?.gt(0) ? '-' : ''}
                <AbbrLimit
                  currency={isEstimate ? displayCurrency : value.TargetAsset}
                  value={toBig(shortPositionLimit)?.abs().toFixed()}
                />
              </Text>
            }
            middle={
              <>
                <AbbrLimit value="0" />
              </>
            }
            right={
              <Text
                fontStyle={isUpdatedRecord?.LongPositionLimit || isUpdatedRecord?.TargetAsset ? 'italic' : undefined}
                display="flex"
              >
                {isEstimate ? '~' : ''}
                <AbbrLimit
                  currency={isEstimate ? displayCurrency : value.TargetAsset}
                  value={toBig(longPositionLimit)?.abs().toFixed()}
                />
              </Text>
            }
          />
        </Flex>
      </JITTooltip>
    </VStack>
  );
}

function getPositionLimits({
  value,
  displayCurrency,
  displayCurrencyConversion,
}: {
  value: PositionThresholdMeterValuesArgs;
  displayCurrency: string | undefined;
  displayCurrencyConversion: CurrencyConversionRate | undefined;
}) {
  if (displayCurrency && displayCurrency !== value.TargetAsset && displayCurrencyConversion?.Rate) {
    const shortPositionLimit = toBigWithDefault(value.ShortPositionLimit, 0).times(displayCurrencyConversion.Rate);
    const longPositionLimit = toBigWithDefault(value.LongPositionLimit, 0).times(displayCurrencyConversion.Rate);
    return { shortPositionLimit, longPositionLimit, isEstimate: true };
  }
  const shortPositionLimit = toBigWithDefault(value.ShortPositionLimit, 0);
  const longPositionLimit = toBigWithDefault(value.LongPositionLimit, 0);
  return { shortPositionLimit, longPositionLimit, isEstimate: false };
}

function PositionThresholdMeterTooltipContent({ value }: PositionThresholdMeterProps) {
  const { CurrentPosition, TargetAsset, LongPositionLimit, ShortPositionLimit, TargetPosition } = value;

  return (
    <Table data-testid="position-table">
      <Tbody>
        <Tr data-testid="short-limit-threshold">
          <Th>Short Limit Threshold</Th>
          <Td align="right">
            <InlineFormattedNumber round number={ShortPositionLimit} currency={TargetAsset} />
          </Td>
        </Tr>
        <Tr data-testid="target-position">
          <Th>Target Position</Th>
          <Td align="right">
            <InlineFormattedNumber round number={TargetPosition} currency={TargetAsset} />
          </Td>
        </Tr>
        <Tr data-testid="long-limit-threshold">
          <Th>Long Limit Threshold</Th>
          <Td align="right">
            <InlineFormattedNumber round number={LongPositionLimit} currency={TargetAsset} />
          </Td>
        </Tr>
        <Tr data-testid="current-position">
          <Th>Current Position</Th>
          <Td align="right">
            <InlineFormattedNumber round number={CurrentPosition} currency={TargetAsset} />
          </Td>
        </Tr>
      </Tbody>
    </Table>
  );
}

export const iconByHedgePositionStatus: Record<IHedgePositionStatus['HedgeControlStatus'], IconName> = {
  Disabled: IconName.PauseCircle,
  Error: IconName.CloseCircle,
  Rejected: IconName.ExclamationCircleSolid,
  Hedging: IconName.CheckCircle,
  Waiting: IconName.Clock,
  Disabling: IconName.DotsVertical,
  Incomplete: IconName.CircleDotted,
  Complete: IconName.CheckCircle,
  Synchronizing: IconName.Refresh,
  Tripped: IconName.Scale,
};

export const iconColorByHedgePositionStatus: Record<IHedgePositionStatus['HedgeControlStatus'], Leaves<Colors>> = {
  Disabled: 'gray.090',
  Error: 'red.lighten',
  Rejected: 'yellow.lighten',
  Hedging: 'green.lighten',
  Waiting: 'blue.lighten',
  Disabling: 'gray.060',
  Incomplete: 'gray.090',
  Complete: 'green.lighten',
  Synchronizing: 'blue.lighten',
  Tripped: 'yellow.lighten',
};
